/**
 * Microsoft Clarity event/tag wrapper, mirrors `lib/mixpanel.js`'s shape so
 * `track()` can forward to both projects without leaking SDK details to
 * callers.
 *
 * Why a wrapper:
 *   - The Clarity script may not be on the page (blocked domain/route,
 *     see `providers/ClarityProvider.jsx`) or may not have booted yet on
 *     the first render. Every entry point guards on `window.clarity`
 *     existing so callers never need to.
 *   - Clarity's two-call API (`clarity("event", name)` for funnel steps,
 *     `clarity("set", key, value)` for session tags) is the same API used
 *     for filtering recordings, wrapping it here keeps the rule "tags
 *     must be strings" in one place.
 *   - The free Clarity tier caps custom event NAMES at ~32 per project.
 *     The Mixpanel funnel layer only emits ~12 distinct names, so we
 *     forward all of them. PROPERTIES become tags (unlimited keys, but
 *     each value is coerced to a short string, see `claritySet`).
 */

/** True iff the Clarity script has loaded and exposed its global. */
function clarityReady() {
  return typeof window !== "undefined" && typeof window.clarity === "function";
}

/**
 * Fire a Clarity custom event. Used as a funnel step in the Clarity
 * dashboard ("Funnels" report) and as a filter on Session Recordings.
 * Silent no-op when Clarity isn't loaded (blocked domain/route, or
 * pre-boot first render).
 */
export function clarityEvent(name) {
  if (!name || !clarityReady()) return;
  try {
    window.clarity("event", String(name));
  } catch {
    /* never let analytics crash the page */
  }
}

/**
 * Tag the current Clarity session with a key=value pair. These appear
 * as "Custom Tags" in the recording UI and can be used to filter both
 * recordings and funnels (e.g., funnel `service_select` filtered by
 * tag `service_name=Title Verification`).
 *
 * Value rules:
 *   - null / undefined / empty string → tag is skipped (Clarity would
 *     render the literal "undefined" text otherwise, same bug we hit
 *     with Mixpanel `$email`).
 *   - Booleans/numbers are coerced to strings.
 *   - Long strings are truncated to 255 chars (Clarity's own limit; if
 *     the SDK silently drops longer values, debugging is a nightmare,
 *     better to truncate visibly here).
 */
export function claritySet(key, value) {
  if (!key || !clarityReady()) return;
  if (value === null || value === undefined || value === "") return;
  try {
    const str = String(value).slice(0, 255);
    window.clarity("set", String(key), str);
  } catch {
    /* never let analytics crash the page */
  }
}

/**
 * Tie the Clarity session to the logged-in customer. The first arg is a
 * stable user id (Mongo `_id` from Redux `customer.Info`), Clarity uses
 * it to merge multi-session recordings under one user in the dashboard.
 * Friendly name/phone are optional; passing them populates the "Identify
 * User" card on the recording page so support can find the session
 * without a SQL lookup.
 *
 * Schema: clarity("identify", customId, customSessionId, customPageId, friendlyName)
 * We don't have meaningful session/page ids to pass, so leave those
 * undefined and Clarity falls back to its own.
 */
export function clarityIdentify(userId, friendlyName) {
  if (!userId || !clarityReady()) return;
  try {
    window.clarity("identify", String(userId), undefined, undefined, friendlyName ? String(friendlyName) : undefined);
  } catch {
    /* never let analytics crash the page */
  }
}

/**
 * Whitelist of Mixpanel property keys that are worth forwarding to
 * Clarity as session tags. Tags are for HUMAN filtering of recordings
 * and funnels, high-cardinality numeric IDs are useless here, so the
 * list is intentionally short. Anything not in this list is dropped at
 * the mixpanel.track() boundary.
 *
 * Add a key only if a marketer/PM would plausibly filter recordings by
 * it. Don't add timestamps, ids, or anything that uniquely identifies
 * the event (those belong in Mixpanel, not session tags).
 */
const TAG_WHITELIST = new Set([
  // Funnel routing
  "page",
  "item",
  "trigger",
  "step",
  // Service/document context
  "service",
  "service_name",
  "service_id",
  "category",
  "doc_type",
  "copy_type",
  // State/intent
  "state",
  "tab",
  "intentLevel",
  "is_new_user",
  "userType",
  // Intelligence / payment
  "action",
  "surface",
  "product",
  "amount",
  "price",
  "credits",
]);

/**
 * Forward a set of Mixpanel properties as Clarity session tags. Filters
 * through TAG_WHITELIST so we don't pollute the tag space (Clarity shows
 * every tag ever set on a session in the recording UI, noise hurts).
 */
export function clarityTagProps(props = {}) {
  if (!clarityReady() || !props) return;
  for (const key of Object.keys(props)) {
    if (!TAG_WHITELIST.has(key)) continue;
    claritySet(key, props[key]);
  }
}
